import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Modal, message, Typography, Space, Select, Tag, Alert, Progress, Tooltip, Divider, Upload, Table } from 'antd';
import { GlobalOutlined, SendOutlined, RocketOutlined, ClearOutlined, CheckCircleOutlined, InfoCircleOutlined, PhoneOutlined, UploadOutlined, UserAddOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import apiClient from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const BroadcastManager = () => {
  const [form] = Form.useForm();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const [account, setAccount] = useState(null);
  const [selectedWaba, setSelectedWaba] = useState(null);
  const [importedContacts, setImportedContacts] = useState([]);
  const [syncingLeads, setSyncingLeads] = useState(false);

  useEffect(() => {
    loadAccount();
  }, []);

  const loadAccount = async () => {
    try {
      const res = await apiClient.get('/accounts/me');
      if (res.data.success) {
        setAccount(res.data.account);
        // Default to first active WABA
        const firstWaba = res.data.account.whatsappNumbers?.find(n => n.isActive)?.wabaId || res.data.account.cloudApiConfig?.businessAccountId;
        if (firstWaba) {
          setSelectedWaba(firstWaba);
          fetchTemplates(firstWaba);
        }
      }
    } catch (error) {
      message.error('Failed to load account details');
    }
  };

  const fetchTemplates = async (wabaId) => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/whatsapp/templates?wabaId=${wabaId}`);
      if (res.data.success) {
        setTemplates(res.data.data.filter(t => t.status === 'APPROVED'));
      }
    } catch (error) {
      console.error('Failed to fetch templates');
      message.error('Failed to fetch templates for selected WABA');
    } finally {
      setLoading(false);
    }
  };

  const handleWabaChange = (wabaId) => {
    setSelectedWaba(wabaId);
    setTemplates([]);
    form.setFieldsValue({ template: undefined });
    fetchTemplates(wabaId);
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      const lines = content.split('\n');
      const contacts = [];
      
      // Basic CSV parsing (assuming headers: name,phone or just phone)
      const header = lines[0].toLowerCase().split(',').map(h => h.trim());
      const nameIdx = header.indexOf('name');
      const phoneIdx = header.indexOf('phone');

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].split(',').map(s => s.trim());
        if (line.length < 1 || !line[0]) continue;

        let name = '';
        let phone = '';

        if (nameIdx !== -1 && phoneIdx !== -1) {
          name = line[nameIdx] || '';
          phone = line[phoneIdx] || '';
        } else {
          // Fallback: first col is phone if no headers found
          phone = line[0];
          name = line[1] || '';
        }

        // Clean phone number (keep digits only)
        phone = phone.replace(/\D/g, '');
        if (phone) {
          contacts.push({ key: Date.now() + i, name, phone });
        }
      }

      setImportedContacts(prev => [...prev, ...contacts]);
      message.success(`Imported ${contacts.length} contacts`);
    };
    reader.readAsText(file);
    return false; // Prevent auto-upload
  };

  useEffect(() => {
    // Sync recipients field with imported contacts
    const numbers = importedContacts.map(c => c.phone).join('\n');
    form.setFieldsValue({ recipients: numbers });
  }, [importedContacts, form]);

  const syncLeads = async () => {
    if (importedContacts.length === 0) return;
    setSyncingLeads(true);
    try {
      let importedCount = 0;
      for (const contact of importedContacts) {
        await apiClient.post('/leads', {
          name: contact.name,
          phone: contact.phone,
          source: 'broadcast_import'
        });
        importedCount++;
      }
      message.success(`${importedCount} contacts converted to Leads!`);
    } catch (error) {
      message.error('Failed to sync some leads');
    } finally {
      setSyncingLeads(false);
    }
  };

  const deleteContact = (key) => {
    setImportedContacts(prev => prev.filter(c => c.key !== key));
  };

  const onFinish = async (values) => {
    const numbers = values.recipients.split('\n')
      .map(n => n.trim())
      .filter(n => n.length > 0);
    
    if (numbers.length === 0) {
      return message.warn('No recipients found');
    }

    const templateObj = templates.find(t => t.name === values.template);
    if (!templateObj) return;

    const wabaConfig = account.whatsappNumbers?.find(n => n.wabaId === selectedWaba) || account.cloudApiConfig;

    Modal.confirm({
      title: '🚀 Launch Campaign?',
      content: (
        <div>
          <p>Sending <b>"{values.template}"</b> to <b>{numbers.length}</b> recipients.</p>
          <p>Source Number: <b>{wabaConfig.displayNumber || wabaConfig.phoneNumberId}</b></p>
          <p>Are you ready to blast?</p>
        </div>
      ),
      okText: 'Blast Launch',
      okType: 'primary',
      onOk: async () => {
        setSending(true);
        setProgress(0);
        setStats(null);
        
        try {
          const payload = {
            name: values.campaignName || 'Unnamed Blast',
            fromPhoneNumberId: wabaConfig.phoneNumberId,
            template: {
              name: values.template,
              language: { code: templateObj.language },
              components: [] // Simple send, can be expanded for variables
            },
            recipients: numbers.map(n => ({ to: n }))
          };
          
          const res = await apiClient.post('/whatsapp/campaign', payload);
          if (res.data.success) {
            setStats({ 
              sent: res.data.sentCount, 
              failed: res.data.failedCount, 
              total: numbers.length, 
              results: res.data.results 
            });
            message.success('Campaign broadcast completed!');
          }
        } catch (error) {
          message.error('Broadcast failed: ' + (error.response?.data?.message || error.message));
        } finally {
          setSending(false);
        }
      }
    });
  };

  const wabaOptions = account?.whatsappNumbers?.map(n => ({
    label: `${n.displayNumber || n.phoneNumberId} (${n.wabaId})`,
    value: n.wabaId,
    disabled: !n.isActive
  })) || [];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: 32 }}>
        <Title level={2}><RocketOutlined /> Marketing Broadcasts</Title>
        <Paragraph type="secondary">
          Launch mass-messaging campaigns through the official Cloud API. 
          Use approved templates to reach customers instantly.
        </Paragraph>
      </div>

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card title={<span><GlobalOutlined /> Campaign Configuration</span>} style={{ marginBottom: 24 }} className="glass-card">
          <Form.Item label="Select WABA Account" required tooltip="Select which WhatsApp Business Account to use for this campaign">
            <Select 
              placeholder="Select a Business Account" 
              size="large"
              value={selectedWaba}
              onChange={handleWabaChange}
              options={wabaOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item name="campaignName" label="Campaign Name">
            <Input placeholder="e.g. March Policy Renewals" prefix={<GlobalOutlined />} />
          </Form.Item>
          
          <Form.Item name="template" label="Select Approved Template" rules={[{ required: true }]} tooltip="Only templates approved by Meta are listed here.">
            <Select 
              placeholder={loading ? "Fetching templates..." : "Choose one..."} 
              size="large" 
              loading={loading}
              disabled={!selectedWaba}
            >
              {templates.map(t => (
                <Option key={t.id} value={t.name}>
                  {t.name} <Tag color="blue" style={{ marginLeft: 8 }}>{t.language}</Tag> <Tag color="purple">{t.category}</Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Alert 
            message="Compliant Template Usage" 
            description={
              <div>
                Campaigns are limited by your Meta Messaging Tier (e.g., 1k/day). Failed sends will be logged.
                <Divider style={{ margin: '8px 0' }} />
                <Text type="secondary" size="small">
                  <InfoCircleOutlined /> <b>Note:</b> These are "Marketing/Utility" templates approved by Meta, different from the "AI Bot Templates" used for automated conversation logic.
                </Text>
              </div>
            }
            type="info" 
            showIcon 
          />
        </Card>

        <Card title={<span><UploadOutlined /> Import from CSV</span>} style={{ marginBottom: 24 }} className="glass-card">
          <Upload.Dragger
            accept=".csv"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            style={{ padding: 20 }}
          >
            <p className="ant-upload-drag-icon"><UploadOutlined /></p>
            <p className="ant-upload-text">Click or drag CSV to this area to import recipients</p>
            <p className="ant-upload-hint">Format: name,phone (One contact per line)</p>
          </Upload.Dragger>

          {importedContacts.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12, alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>Preview & Edit ({importedContacts.length})</Title>
                <Space>
                  <Button 
                    icon={<UserAddOutlined />} 
                    onClick={syncLeads} 
                    loading={syncingLeads}
                    type="dashed"
                  >
                    Convert to CRM Leads
                  </Button>
                  <Button danger icon={<ClearOutlined />} onClick={() => setImportedContacts([])}>Clear All</Button>
                </Space>
              </div>
              <Table 
                dataSource={importedContacts} 
                size="small" 
                pagination={{ pageSize: 5 }}
                columns={[
                  { title: 'Name', dataIndex: 'name', key: 'name', render: (text, record) => (
                    <Input 
                      value={text} 
                      size="small" 
                      bordered={false} 
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setImportedContacts(prev => prev.map(c => c.key === record.key ? { ...c, name: newVal } : c));
                      }}
                    />
                  )},
                  { title: 'Phone', dataIndex: 'phone', key: 'phone', render: (text, record) => (
                    <Input 
                      value={text} 
                      size="small" 
                      bordered={false}
                      onChange={(e) => {
                        const newVal = e.target.value;
                        setImportedContacts(prev => prev.map(c => c.key === record.key ? { ...c, phone: newVal } : c));
                      }}
                    />
                  )},
                  { title: 'Action', key: 'action', width: 60, render: (_, record) => (
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => deleteContact(record.key)} />
                  )}
                ]}
              />
            </div>
          )}
        </Card>

        <Card title={<span><PhoneOutlined /> Target Recipients</span>} style={{ marginBottom: 24 }} className="glass-card">
          <Form.Item 
            name="recipients" 
            label="Phone Numbers (One per Line, include country code)" 
            rules={[{ required: true, message: 'Please input recipients' }]}
            help="Example: 919999999999. If you import a CSV, this field will auto-populate."
          >
            <TextArea rows={8} placeholder="91XXXXXXXXXX\n91YYYYYYYYYY" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
          <div style={{ textAlign: 'right' }}>
            <Button icon={<ClearOutlined />} onClick={() => {
              form.setFieldsValue({ recipients: '' });
              setImportedContacts([]);
            }}>Clear Numbers</Button>
          </div>
        </Card>

        {sending && (
          <Card style={{ marginBottom: 24 }}>
            <Progress percent={99} status="active" strokeColor={{ '0%': '#108ee9', '100%': '#87d068' }} />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <Text strong>Executing broadcast campaign... please don't close this window.</Text>
            </div>
          </Card>
        )}

        {stats && (
          <Card 
            title="🏁 Campaign Results" 
            style={{ marginBottom: 24, border: '2px solid #52c41a', background: '#f6ffed' }}
            extra={<Button type="link" onClick={() => setStats(null)}>Dismiss</Button>}
          >
             <Space size={64} style={{ width: '100%', justifyContent: 'center' }}>
               <div style={{ textAlign: 'center' }}>
                 <Title level={2} style={{ margin: 0, color: '#52c41a' }}>{stats.sent}</Title>
                 <Text type="secondary" strong>SENT SUCCESS</Text>
               </div>
               <div style={{ textAlign: 'center' }}>
                 <Title level={2} style={{ margin: 0, color: '#f5222d' }}>{stats.failed}</Title>
                 <Text type="secondary" strong>FAILED</Text>
               </div>
               <div style={{ textAlign: 'center' }}>
                 <Title level={2} style={{ margin: 0 }}>{stats.total}</Title>
                 <Text type="secondary" strong>TOTAL TARGET</Text>
               </div>
             </Space>
             
             {stats.failed > 0 && (
               <div style={{ marginTop: 16 }}>
                 <Title level={5}>Failure Log (Preview):</Title>
                 <ul style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #ffa39e', padding: '8px 24px', borderRadius: 4, background: '#fff2f0' }}>
                   {stats.results.filter(r => r.status === 'failed').map((r, i) => (
                     <li key={i}><Text type="danger">{r.to}: {r.error}</Text></li>
                   ))}
                 </ul>
               </div>
             )}
          </Card>
        )}

        <div style={{ textAlign: 'center', paddingTop: 16 }}>
          <Button 
            type="primary" 
            htmlType="submit" 
            size="large" 
            icon={<RocketOutlined />} 
            loading={sending}
            disabled={!selectedWaba || sending}
            style={{ 
              width: 280, 
              height: 55, 
              fontSize: 18, 
              borderRadius: 28, 
              fontWeight: 'bold',
              boxShadow: '0 4px 14px 0 rgba(24,144,255,0.39)',
              background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)', 
              border: 'none' 
            }}
          >
            {sending ? 'Launching...' : 'Blast Campaign Now'}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default BroadcastManager;
